import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useState } from "react";
import { PanelLoading } from "../../components/Loading";
import { useTranslation } from "react-i18next";
import { erpApi, type ErpOutboxRow, type ErpOutboxStatus } from "../../api/erp";
import type { ApiError } from "../../api/client";
import { useToast } from "../../components/Toast";
import { Select } from "../../components/Select";

const STATUSES: ErpOutboxStatus[] = ["PENDING", "SENT", "FAILED"];

export default function AdminErpOutboxPage() {
  const { canWrite } = useSectionAccess("erp_operations");
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [rows, setRows] = useState<ErpOutboxRow[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<ErpOutboxStatus | "">("");

  async function load() {
    try {
      setRows(await erpApi.list(statusFilter || undefined));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }
  useEffect(() => { void load(); }, [statusFilter]);

  async function retry(row: ErpOutboxRow) {
    try {
      await erpApi.retry(row.id);
      toast.success(t("admin.erpOutbox.toast.requeued"));
      await load();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>{t("admin.erpOutbox.title")}</h1>
        <button type="button" onClick={load} style={secondary}>{t("admin.erpOutbox.refresh")}</button>
      </header>

      <div style={{ display: "flex", gap: 12 }}>
        <Select<ErpOutboxStatus | "">
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ maxWidth: 200 }}
          options={[
            { value: "", label: t("admin.erpOutbox.allStatuses") },
            ...STATUSES.map((s) => ({ value: s, label: t(`admin.erpOutbox.status.${s}`) })),
          ]}
        />
      </div>

      {!rows ? (
        <PanelLoading text={t("common.loading")} />
      ) : rows.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.erpOutbox.empty")}</p>
      ) : (
        <table style={tbl}>
          <thead>
            <tr>
              <th style={th}>{t("admin.erpOutbox.col.order")}</th>
              <th style={th}>{t("admin.erpOutbox.col.company")}</th>
              <th style={th}>{t("admin.erpOutbox.col.kind")}</th>
              <th style={th}>{t("admin.erpOutbox.col.status")}</th>
              <th style={th}>{t("admin.erpOutbox.col.attempts")}</th>
              <th style={th}>{t("admin.erpOutbox.col.nextAttempt")}</th>
              <th style={th}>{t("admin.erpOutbox.col.erpRef")}</th>
              <th style={th}>{t("admin.erpOutbox.col.lastError")}</th>
              <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td style={td}><code>{r.orderNumber ?? r.contractReference ?? "—"}</code></td>
                <td style={td}>{r.companyName ?? "—"}</td>
                <td style={td}>{t(`admin.erpOutbox.kind.${r.kind}`, r.kind)}</td>
                <td style={td}><StatusBadge status={r.status} /></td>
                <td style={td}>{r.attempts}</td>
                <td style={td}>{new Date(r.nextAttemptAt).toLocaleString(i18n.language)}</td>
                <td style={td}>{r.erpReference ? <code style={{ fontSize: 11 }}>{r.erpReference}</code> : "—"}</td>
                <td style={{ ...td, color: "var(--err)", fontSize: 12, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.lastError ?? undefined}>
                  {r.lastError ?? "—"}
                </td>
                <td style={td}>
                  {r.status !== "SENT" && (
                    <button type="button" disabled={!canWrite} onClick={() => retry(r)} style={linkBtn}>{t("admin.erpOutbox.action.retry")}</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: ErpOutboxStatus }) {
  const { t } = useTranslation();
  const palette: Record<ErpOutboxStatus, { bg: string; fg: string }> = {
    PENDING: { bg: "var(--warn-bg)", fg: "var(--warn)" },
    SENT:    { bg: "var(--ok-bg)", fg: "var(--ok)" },
    FAILED:  { bg: "var(--err-bg)", fg: "var(--err)" },
  };
  // A state the browser does not know is a state the server added since this bundle was built,
  // which is exactly what a reader with an open tab sees during a release. An unknown state is
  // shown plainly rather than taking the screen down with it.
  const c = palette[status] ?? { bg: "var(--surface-2)", fg: "var(--ink-2)" };
  return <span style={{ background: c.bg, color: c.fg, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{t(`admin.erpOutbox.status.${status}`)}</span>;
}

const tbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13, background: "var(--surface)", borderRadius: 8, overflow: "hidden", boxShadow: "var(--shadow-1)" };
const th: React.CSSProperties = { textAlign: "left", padding: "10px 12px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--border)" };
const secondary: React.CSSProperties = { padding: "8px 14px", background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const linkBtn: React.CSSProperties = { background: "transparent", border: "none", color: "var(--brand-ink)", cursor: "pointer", fontSize: 13 };
